import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = auth, false = not auth

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth callback — let AuthCallback establish session first.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) { console.error("Logout request failed:", e); }
    setUser(false);
    window.location.href = "/";
  };

  // Real-time push via WebSocket (short-lived ws-token in query, since the JWT cookie is httpOnly).
  useEffect(() => {
    if (!user || user === false) return;
    let ws;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/ws-token");
        if (cancelled) return;
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
        ws = new WebSocket(`${base}/api/ws?token=${encodeURIComponent(data.token)}`);
        ws.onmessage = (e) => {
          try {
            const d = JSON.parse(e.data);
            window.dispatchEvent(new CustomEvent(`bidmove:${d.event}`, { detail: d }));
            if (d.event === "notification" && d.ntype === "approval") checkAuth();
          } catch (err) { console.error("WS message parse failed:", err); }
        };
      } catch (err) { console.error("WS connect failed:", err); }
    })();
    return () => { cancelled = true; try { ws && ws.close(); } catch (err) { console.error("WS close failed:", err); } };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
